import { useEffect, useRef, useState } from 'react';
import { LegalPage, LegalSection } from './LegalPage';
import { AlertIcon, CheckCircleIcon } from '../components/icons';
import { ROUTES } from '../router';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { DELETE_ACCOUNT_SUCCESS_MESSAGE, FORM_PROVIDER, SUPPORT_EMAIL } from '../data/legal';
import './DeleteAccountPage.css';

const ENDPOINT = 'https://api.web3forms.com/submit';

const EMPTY_FORM = { email: '', name: '', reason: '', confirmed: false };

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const validate = (form) => {
  const errors = {};
  if (!form.email.trim()) {
    errors.email = 'Please enter the email address on your Tulink account.';
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Please check this email address — it looks incomplete.';
  }
  if (!form.confirmed) {
    errors.confirmed = 'Please confirm that you want your account and data deleted.';
  }
  return errors;
};

/*
 * Public account-deletion request page.
 *
 * It asks for the account email, an optional name and reason, and an explicit
 * confirmation — nothing else. A password, a location or an ID document would
 * be more sensitive than the account being deleted, so this form never asks for
 * them, and ownership is proved out of band by replying to the account email.
 *
 * The request goes to its own Web3Forms key: a deletion request must reach the
 * configured deletion-request destination and nowhere else, so an unconfigured
 * deployment says so instead of quietly succeeding.
 */
export const DeleteAccountPage = () => {
  useDocumentTitle('Request account deletion — Tulink');

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | error | unconfigured | success
  const fieldRefs = useRef({});
  const successRef = useRef(null);

  // The submit button disappears with the form, so send focus to the outcome
  // rather than leaving the keyboard on a detached node.
  useEffect(() => {
    if (status === 'success' && successRef.current) {
      successRef.current.focus();
    }
  }, [status]);

  const updateField = (key) => (event) => {
    const { type, value, checked } = event.target;
    setForm((previous) => ({ ...previous, [key]: type === 'checkbox' ? checked : value }));
    setErrors((previous) => {
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validate(form);
    setErrors(nextErrors);

    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      const node = fieldRefs.current[firstInvalid];
      if (node) node.focus();
      return;
    }

    const accessKey = process.env.REACT_APP_ACCOUNT_DELETION_ACCESS_KEY;
    if (!accessKey) {
      setStatus('unconfigured');
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: accessKey,
          subject: 'Tulink account deletion request',
          from_name: 'Tulink account deletion',
          // `email` is Web3Forms' reserved user-email field: it sets the
          // reply-to on the forwarded mail and is what the autoresponder needs.
          // `account_email` repeats it under a label that reads clearly in the
          // email body.
          email: form.email.trim(),
          account_email: form.email.trim(),
          name: form.name.trim() || 'Not provided',
          reason: form.reason.trim() || 'Not provided',
          confirmed_deletion_request: 'Yes',
        }),
      });

      // A 200 is not delivery. Web3Forms reports the real outcome in the JSON
      // body, so a `success: false` payload — or a body that will not parse —
      // is a failure the person needs to see, not a silent drop.
      let payload = null;
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = null;
      }

      setStatus(response.ok && payload?.success === true ? 'success' : 'error');
    } catch (requestError) {
      setStatus('error');
    }
  };

  const submitting = status === 'submitting';

  // One live region, mounted for the whole life of the page: a region that
  // appears at the same moment as its message is announced unreliably.
  const liveMessage = submitting
    ? 'Sending your deletion request.'
    : status === 'success'
      ? DELETE_ACCOUNT_SUCCESS_MESSAGE
      : '';

  return (
    <LegalPage
      eyebrow="Your account"
      title="Request account deletion"
      lede="Use this page to ask us to delete your Tulink account and associated personal data."
    >
      <LegalSection id="what-happens" title="What happens after you ask">
        <p>
          Send the request below and we will email the address on the account to check that it is
          really yours. That reply explains the next steps and what deletion will remove. We cannot
          act on a request until ownership is confirmed, so keep an eye on that inbox — including the
          spam folder.
        </p>
        <p>
          This form starts the request. It does not delete anything by itself, and we do not promise
          a completion date here until Tulink has confirmed one.
        </p>
        {/*
          A plain anchor, not a PageLink: this link carries a fragment, and the
          browser's own navigation lands on the clause and moves focus there.
          Teaching the router to do that would be more machinery than one link
          is worth.
        */}
        <p>
          Before you send it, you may want to read the{' '}
          <a href={`${ROUTES.privacy}#deletion`}>deletion section of the Privacy Policy</a>.
        </p>
      </LegalSection>

      <LegalSection id="request-form" title="Your request">
        <p className="tl-delete__scope">
          We only need your account email. Never send us your password, a photo of an ID document or
          your location — we will never ask for any of them, and we do not need them to verify this
          request.
        </p>

        <p className="tl-visually-hidden" role="status">
          {liveMessage}
        </p>

        {status === 'success' ? (
          <div className="tl-delete__success">
            <span className="tl-delete__success-icon" aria-hidden="true">
              <CheckCircleIcon size={28} />
            </span>
            <h3 id="tl-delete-success" ref={successRef} tabIndex={-1}>
              {DELETE_ACCOUNT_SUCCESS_MESSAGE}
            </h3>
            <p>
              We have sent this request to the Tulink team. The verification email will go to{' '}
              <strong>{form.email.trim()}</strong>.
            </p>
          </div>
        ) : (
          <form className="tl-delete__form" onSubmit={handleSubmit} noValidate>
            <div className="tl-field">
              <label className="tl-field__label" htmlFor="tl-delete-email">
                Account email address <span className="tl-field__required">(required)</span>
              </label>
              <p className="tl-field__hint" id="tl-delete-email-hint">
                The address you use to sign in to Tulink. We will send the verification email here.
              </p>
              <input
                className={`tl-field__input${errors.email ? ' has-error' : ''}`}
                id="tl-delete-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={updateField('email')}
                aria-invalid={errors.email ? 'true' : undefined}
                aria-describedby={
                  errors.email ? 'tl-delete-email-hint tl-delete-email-error' : 'tl-delete-email-hint'
                }
                ref={(node) => {
                  fieldRefs.current.email = node;
                }}
              />
              {errors.email && (
                <p className="tl-field__error" id="tl-delete-email-error" role="alert">
                  <AlertIcon size={16} />
                  {errors.email}
                </p>
              )}
            </div>

            <div className="tl-field">
              <label className="tl-field__label" htmlFor="tl-delete-name">
                Your name <span className="tl-field__optional">(optional)</span>
              </label>
              <input
                className="tl-field__input"
                id="tl-delete-name"
                name="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={updateField('name')}
              />
            </div>

            <div className="tl-field">
              <label className="tl-field__label" htmlFor="tl-delete-reason">
                Reason for leaving <span className="tl-field__optional">(optional)</span>
              </label>
              <p className="tl-field__hint" id="tl-delete-reason-hint">
                It helps us improve Tulink, but your request is processed either way.
              </p>
              <textarea
                className="tl-field__input"
                id="tl-delete-reason"
                name="reason"
                rows={4}
                value={form.reason}
                onChange={updateField('reason')}
                aria-describedby="tl-delete-reason-hint"
              />
            </div>

            <div className="tl-field">
              <label className={`tl-checkbox${errors.confirmed ? ' has-error' : ''}`}>
                <input
                  type="checkbox"
                  name="confirmed"
                  required
                  checked={form.confirmed}
                  onChange={updateField('confirmed')}
                  aria-invalid={errors.confirmed ? 'true' : undefined}
                  aria-describedby={errors.confirmed ? 'tl-delete-confirm-error' : undefined}
                  ref={(node) => {
                    fieldRefs.current.confirmed = node;
                  }}
                />
                <span className="tl-checkbox__label">
                  I am asking Tulink to delete my account and the personal data associated with it.
                  I understand this cannot be undone once it is carried out.{' '}
                  <span className="tl-field__required">(required)</span>
                </span>
              </label>
              {errors.confirmed && (
                <p className="tl-field__error" id="tl-delete-confirm-error" role="alert">
                  <AlertIcon size={16} />
                  {errors.confirmed}
                </p>
              )}
            </div>

            {status === 'error' && (
              <p className="tl-notice tl-notice--error" role="alert">
                <AlertIcon size={18} />
                We could not send your request just now. Please check your connection and try again,
                or email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> instead.
              </p>
            )}

            {status === 'unconfigured' && (
              <p className="tl-notice tl-notice--error" role="alert">
                <AlertIcon size={18} />
                This form is not available at the moment. Please email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from your account email
                address and we will handle your request that way.
              </p>
            )}

            <button
              type="submit"
              className="tl-button tl-button--primary tl-delete__submit"
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Request account deletion'}
            </button>
          </form>
        )}
      </LegalSection>

      <LegalSection id="where-it-goes" title="Where this request goes">
        <p>
          This form is delivered to the Tulink team by{' '}
          <a href={FORM_PROVIDER.privacyUrl} target="_blank" rel="noopener noreferrer">
            {FORM_PROVIDER.name}
          </a>
          , which forwards it by email to the Tulink team.
        </p>
        <p>
          If you would rather not use this form, email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from your account email address
          instead.
        </p>
      </LegalSection>
    </LegalPage>
  );
};
